import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Paperclip, Plus, Send, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const TONE = { new: ["Not started", "bg-slate-100 text-slate-700"], queued: ["Queued", "bg-blue-50 text-blue-800"], applying: ["Applying...", "bg-blue-50 text-blue-800"],
  applied: ["Applied", "bg-emerald-50 text-emerald-800"], approved: ["Approved", "bg-emerald-100 text-emerald-900"], failed: ["Needs your help", "bg-red-50 text-red-700"],
  payment_due: ["Fee due - see Payments", "bg-amber-50 text-amber-800"] };

const Site = ({ s, act }) => {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [label, cls] = TONE[s.status] || TONE.new;
  const send = async () => {
    const fd = new FormData();
    fd.append("text", note);
    files.forEach((f) => fd.append("files", f));
    if (await act(() => api.post(`/rt/reo/sites/${s.id}/instructions`, fd), "Ava will try again with what you sent")) { setNote(""); setFiles([]); }
  };
  return (
    <div className="border-t border-slate-100 py-3 text-sm" data-testid={`reo-site-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0"><div className="font-semibold text-slate-900">{s.name}</div>
          <a href={s.url} target="_blank" rel="noreferrer" className="break-all text-xs text-blue-700 underline">{s.url}</a>
          {s.notes && <div className="text-xs text-slate-500">{s.notes}</div>}</div>
        <div className="flex items-center gap-1">
          <Badge className={`border-0 ${cls}`} data-testid={`reo-site-status-${s.id}`}>{label}</Badge>
          {s.status === "applied" && <Button size="sm" variant="ghost" onClick={() => act(() => api.put(`/rt/reo/sites/${s.id}/status`, { status: "approved" }), "Marked approved")} data-testid={`reo-site-approve-${s.id}`}><CheckCircle2 className="mr-1 h-4 w-4" />Approved</Button>}
          <Button size="icon" variant="ghost" onClick={() => window.confirm(`Remove ${s.name}?`) && act(() => api.delete(`/rt/reo/sites/${s.id}`), "Removed")} data-testid={`reo-site-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      </div>
      {s.last_error && s.status === "failed" && <p className="mt-1 text-xs text-red-700" data-testid={`reo-site-error-${s.id}`}>{s.last_error}</p>}
      {s.result_note && ["applied", "approved"].includes(s.status) && <p className="mt-1 text-xs text-slate-500">{s.result_note}</p>}
      {s.files?.length > 0 && <p className="mt-1 flex items-center gap-1 text-xs text-slate-500" data-testid={`reo-site-files-${s.id}`}><Paperclip className="h-3 w-3" />{s.files.map((f) => f.filename).join(", ")}</p>}
      {["failed", "applied", "queued"].includes(s.status) && <div className="mt-2">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell Ava what to do: missing details, your existing login for this site, or reply to her email" data-testid={`reo-site-note-${s.id}`} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid={`reo-site-attach-${s.id}`}>
            <Paperclip className="h-3 w-3" />Attach files
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { setFiles([...e.target.files]); e.target.value = ""; }} data-testid={`reo-site-file-${s.id}`} /></label>
          {files.length > 0 && <span className="text-xs text-slate-600" data-testid={`reo-site-picked-${s.id}`}>{files.map((f) => f.name).join(", ")}</span>}
          <Button size="sm" className="ml-auto" onClick={send} disabled={note.trim().length < 2 && !files.length} data-testid={`reo-site-note-send-${s.id}`}><Send className="mr-1 h-4 w-4" />Send to Ava</Button>
        </div></div>}
    </div>
  );
};

const AddSite = ({ act }) => {
  const [f, setF] = useState({ name: "", url: "", notes: "" });
  const add = async () => { if (await act(() => api.post("/rt/reo/sites", f), "Website added")) setF({ name: "", url: "", notes: "" }); };
  return (
    <div className="mt-4 grid gap-2 border-t border-slate-100 pt-4 sm:grid-cols-[1fr_1.5fr_1.5fr_auto]">
      <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="reo-add-name" />
      <Input placeholder="https://..." value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="reo-add-url" />
      <Input placeholder="Notes (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="reo-add-notes" />
      <Button onClick={add} data-testid="reo-add-site"><Plus className="mr-1 h-4 w-4" />Add</Button>
    </div>
  );
};

export const ReoSites = ({ d, act }) => (
  <Card className="p-6" data-testid="reo-sites-card">
    <h3 className="font-display text-lg font-bold text-slate-900">REO websites ({d.sites.length})</h3>
    <p className="text-xs text-slate-500">A starter list you can edit. Ava applies to each one and tracks the result.</p>
    {d.sites.map((s) => <Site key={s.id} s={s} act={act} />)}
    <AddSite act={act} />
  </Card>
);
